<template>
  <div>
    <Header>{{ headerText }}</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="flex justify-center gap-2">
        <Button
          color="gray"
          size="md"
          @click="toDriverList()">
          Back to Driver List
        </Button>
      </div>
      <Spinner
        v-if="driverLoading"
        message="Loading Driver Info"
        :size="16"
      />
      <div class="mx-auto my-4 max-w-6xl">
        <ValidationObserver v-slot="{ invalid, validate, errors, reset }">
          <form name="driverForm" class="relative" @submit.prevent="validate().then(() => onSubmit(invalid, reset, false))">
            <DriverActions v-if="driver"
              class="absolute right-4 top-4"
              :driver="driver">
              <Button
                data-testid="form-actions-button"
                type="button"
                color="blue"
                size="md"
                class="rounded-md !shadow m-1"
                >
                Actions
              </Button>
            </DriverActions>
            <div v-if="enableVendorSelect" class="container mb-3">
              <SelectRequiredData
                v-model="formValues.requiredData"
                :includeVendorSelect="true"
                :enableVendorSelect="enableVendorSelect"
                :vendorOptions="vendorOptions"
              >
              </SelectRequiredData>
            </div>
            <div class="container">
              <h2 class="font-medium text-4xl">Profile</h2>
            </div>
            <div class="container mb-3">
              <div class="flex flex-col gap-1">
                <ValidationProvider rules="required" name="first name" v-slot="{ errors }">
                  <TextField
                    class="w-1/3"
                    :id="'driver-first-name'"
                    v-model="formValues.firstName"
                    >
                    First Name
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="container mb-3">
              <div class="flex flex-col gap-1">
                <ValidationProvider rules="required" name="last name" v-slot="{ errors }">
                  <TextField
                    class="w-1/3"
                    :id="'driver-last-name'"
                    v-model="formValues.lastName"
                    >
                    Last Name
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="container mb-3">
              <div class="flex flex-col gap-1">
                <ValidationProvider rules="required|email"  name="email" v-slot="{ errors }">
                  <TextField
                    class="w-1/2"
                    :id="'driver-email'"
                    :type="'email'"
                    v-model="formValues.email"
                    >
                    Email
                  </TextField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }} {{ emailError }}
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="container mb-3">
              <div class="flex flex-col gap-1">
                <ValidationProvider rules="required" name="date of birth" v-slot="{ errors }">
                  <CalendarField
                    id="driver-dob"
                    class="w-40"
                    :max="today"
                    :min="'1900-01-01'"
                    v-model="formValues.dateOfBirth"
                    >
                    Date of birth
                  </CalendarField>
                  <span
                    class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                    name="error-text">
                    {{ errors[0] }}
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="container mb-3">
              <TextArea
                class="w-1/2"
                :id="'driver-bio'"
                v-model="formValues.bio"
              >
                Bio
              </TextArea>
            </div>

            <!-- Documents section only shows once the driver has been created -->
            <div v-if="driver" class="container mt-4">
              <h2 class="font-medium text-4xl">Documents</h2>
              <div v-if="isIncognito" class="mt-2 p-3 bg-yellow-100 border border-yellow-400 rounded text-yellow-800 text-sm">
                Document uploads are not supported in incognito or private browsing mode. Please switch to a regular browser tab to upload files.
              </div>
              <GoogleAuth :clientId="googleCredentials.clientId" v-slot="{ ready, processing, login, withToken, logout, authenticated, profile }" @profile-loaded="(p) => { googleUserEmail = p.email }" @logout="() => { googleUserEmail = null; folderPermission = null }">
                <GoogleDrive :folderId="driverFolderId" :authenticated="authenticated" :credentials="googleCredentials" @fileLoad="(file, type) => copyFile(file, type)" v-slot="{ loading, error, showPicker }">
                  <div v-if="canValidateDriver || editableDocuments">
                    <div v-if="requiredDocTypes">
                      <p v-if="requiredDocTypes.length > 0">Driver documentation is required.</p>
                      <p v-else>No driver documents are required.</p>
                    </div>
                    <div class="flex justify-between">
                      <div class="flex-row space-x-4">
                        <GoogleButton
                          :ready="ready && credentialsReady"
                          :processing="processing"
                          :authenticated="authenticated"
                          @click="authenticated ? logout() : login()"
                        />
                        <span v-if="loading || !credentialsReady">
                          Loading...
                        </span>
                        <span v-else-if="authenticated && profile">
                          Signed in as <strong>{{ profile.name }} &lt;{{ profile.email }}&gt;</strong>
                        </span>
                        <span v-else>
                          Sign in with Google to upload documents
                        </span>
                      </div>
                    </div>
                    <div v-if="folderPermission === false" class="mt-2 p-3 bg-red-100 border border-red-400 rounded text-red-800 text-sm">
                      This Google account does not have permission to access the driver's document folder. Sign in with a different account or contact us at <strong>technology@drivecst.org.</strong>
                    </div>
                    <div v-if="error" class="mt-4">
                      <p class="text-red-500">Error: {{ error }}</p>
                      <div class="mt-4">
                        <div v-if="authenticated">
                          <p>Sign in with a different account or contact us at <strong>technology@drivecst.org.</strong></p>
                        </div>
                        <div v-else>
                          <p>Contact us at <strong>technology@drivecst.org.</strong></p>
                        </div>
                      </div>
                    </div>
                    <div v-if="authenticated && !loading && driver && driver.files && driver.files.length == 0 && files.length == 0" class="mt-4">
                      <p>This driver does not yet have any documents. Click <strong>+ Upload</strong> to get started.</p>
                    </div>
                  </div>
                  <div v-else>
                    <p>Documents are approved and may not be edited at this time.</p>
                  </div>
                  <div :class="{ 'opacity-30': loading || !credentialsReady }">
                    <FileList
                      ref="fileList"
                      v-model="files"
                      :files="files"
                      :docTypes="requiredDocTypes.length !== 0 ? requiredDocTypes : allDocTypes"
                      :canValidate="canValidateDriver"
                      :validated="driver.validated"
                      @showPicker="(value) => withToken('https://www.googleapis.com/auth/drive.file', (token) => showPicker(token, value))"
                      @input="(vals) => files=vals"
                      :editableDocuments="!(error != '' || !authenticated || loading || !credentialsReady) && folderPermission !== false && (canValidateDriver || editableDocuments)"
                      @deleteFile="(fileId) => deleteFile(fileId)"
                    >
                    </FileList>
                  </div>
                </GoogleDrive>
              </GoogleAuth>
            </div>

            <div class="container mt-4">
              <div class="w-full rounded-md bg-gray-200 p-2 my-2"
                v-if="!canSubmit">
                <span class="uppercase text-gray-500">
                  Missing Permissions to Submit Form
                </span>
              </div>
              <div class="flex flex-row">
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-2 ml-1"
                  name="error-text"
                  v-if="displayFormHasErrors(errors)">
                  There are errors in fields above.
                </span>
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-2 ml-1"
                  v-if="missing_docs.length > 0 && submittingForApproval">
                  The following required documents are missing: {{ missing_docs.join(', ') }}.
                </span>
              </div>
              <div class="flex flex-row gap-4 my-2">
                <SubmitButton
                  class="rounded-md font-medium shadow-sm"
                  type="submit"
                  color="blue"
                  size="md"
                  :disabled="!canSubmit"
                  :submitting="saving">
                  {{ saveText }}
                </SubmitButton>
                <SubmitButton
                  class="rounded-md font-medium shadow-sm"
                  type="button"
                  color="blue"
                  size="md"
                  v-if="driver && showSubmitButton"
                  @click="validate().then(() => submitForApproval(invalid, reset))"
                  :submitting="submitting"
                  :disabled="!canSubmit">
                  Submit for Approval
                </SubmitButton>
              </div>
            </div>
          </form>
        </ValidationObserver>
      </div>
      <!-- <p>Endpoint Data: {{ formValues }}</p> -->
      <!-- <p>Default Values: {{ driver }}</p> -->

    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'
import Button from '../components/Button.vue'
import SubmitButton from '../components/SubmitButton.vue'
import TextField from '../components/formComponents/TextField.vue'
import TextArea from '../components/formComponents/TextArea.vue'
import SelectRequiredData from '../components/SelectRequiredData.vue'
import CalendarField from '../components/formComponents/CalendarField.vue'
import DriverActions from '../components/DriverActions.vue'
import GoogleAuth from '../components/GoogleAuth.vue'
import GoogleButton from '../components/GoogleButton.vue'
import GoogleDrive from '../components/GoogleDrive.vue'
import FileList from '../components/FileList.vue'
import Spinner from '../components/Spinner.vue'

import driverFormService from '../services/pages/driverForm.js'

import user from '../services/user/user.js'

import DropdownLists from '../components/DropdownLists.json'

import { detectIncognito } from 'detectincognitojs'

export default {
  components: {
    Main,
    Header,
    Button,
    SubmitButton,
    TextField,
    TextArea,
    SelectRequiredData,
    CalendarField,
    DriverActions,
    GoogleAuth,
    GoogleButton,
    GoogleDrive,
    FileList,
    Spinner,
  },
  props: {
    driverId: String
  },
  data() {
    return {
      user: undefined,
      formValues: {
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: '',
        bio: '',

        requiredData: {},
      },
      files: [],
      driverLoading: false,

      driver: undefined,

      driverFolderId: undefined,

      canValidateDriver: false,

      vendorOptions: [],
      enableVendorSelect: false,

      requiredDocTypes: [],
      missing_docs: [],
      allDocTypes: Object.keys(DropdownLists.DocumentTypesList),

      requiresValidation: true,
      vendorId: undefined,

      canSubmit: true,
      saving: false,
      submitting: false,
      submittingForApproval: false,
      emailError: '',

      googleCredentials: {},
      credentialsReady: false,
      googleUserEmail: null,
      folderPermission: null,

      isIncognito: false,
      
      today: new Date().toISOString().substring(0, 10)
    }
  },
  methods: {
    async loadDriverDetail() {
      this.driver = (await driverFormService.getDriverDetail(this.driverId)).data
      this.files = this.driver.files
    },
    async checkFolderPermission() {
      if (!this.googleUserEmail || !this.driverFolderId) return
      const response = await driverFormService.getGoogleFolderPermission(this.driverFolderId, this.googleUserEmail).catch(() => null)
      this.folderPermission = response ? response.data.has_folder_access : null
    },
     async onSubmit(formInvalid, reset, requiresApproval) {
      let hasError = this.scrollToFirstError()

      if (formInvalid || hasError || (requiresApproval && this.missing_docs.length > 0)) {
        return false
      }
      if(requiresApproval){
        this.submitting = true
      } else {
        this.saving = true
      }

      let submissionPayload = {
        first_name: this.formValues.firstName,
        last_name: this.formValues.lastName,
        email: this.formValues.email,
        vendor_id: this.formValues.requiredData?.vendorId,
        bio: this.formValues.bio,
        date_of_birth: this.formValues.dateOfBirth,
        files: this.files,
        requires_approval: requiresApproval
      }

      this.emailError = ''
      let submitSucceeded = false

      try {
        if (this.driver) {
          this.driver = (await driverFormService.putDriverForm(this.driver.id, submissionPayload)).data
        } else {
          this.driver = (await driverFormService.postDriverForm(submissionPayload)).data
          this.validateProvidedInfo()
          reset()
        }
        this.$router.replace({ name: 'DriverForm', params: { driverId: this.driver.id } }).catch(() => {})
        submitSucceeded = true
      }
      catch (e) {
        console.log(e)
        if (e.response?.data?.error === 'Driver with this email already exists') {
          this.emailError = 'Driver with this email already exists'
        }
      }
      finally {
        this.saving = false
        this.submitting = false
        if (submitSucceeded && this.driver) {
          this.$router.go(0)
        }
      }
    },
    scrollToFirstError() {
      const elems = document.getElementsByName('error-text');
      let hasError = false;
      
      for(let el of Array.from(elems)) {
        if(el.innerText && el.innerText.trim()) {
          hasError = true;
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
          break;
        }
      }

      return hasError;
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0;
    },
    toDriverList() {
      this.$router.push({
        name: 'DriverList',
      });
    },
    async copyFile(newFile, type) {
      const index = this.files.findIndex((file) => file.type === type)

      const fileData = {
        fileName: newFile.name,
        name: newFile.name,
        fileId: newFile.id,
        file_id: newFile.id,
        fileType: type,
        type: type,
      }

      if (index !== -1) {
        this.$set(this.files, index, fileData)
      } else {
        this.files.push(fileData)
      }

      await driverFormService.putDriverFile(this.driver.id, fileData)

      this.driver = (await driverFormService.getDriverDetail(this.driverId)).data
      this.files = [...this.driver.files]
    },
    async deleteFile(fileId){
      let promise = await driverFormService.deleteDriverFile(this.driver.id, fileId)
      if(promise.status == 204){
        const index = this.files.findIndex((file) => file.fileId === fileId || file.file_id === fileId)
        if(index != -1){
          this.$set(this.files[index], 'fileId', '')
          this.$set(this.files[index], 'file_id', '')
          this.$set(this.files[index], 'thumbnailLink', '')
          this.$set(this.files[index], 'id', '')
          this.$set(this.files[index], 'expirationDate', null)
        }
      }
    },
    async getRequiredDocumentTypes(){
      if (this.formValues.requiredData.vendorId){
        this.driverRequirements = (await driverFormService.getDriverRequirements(this.formValues.requiredData.vendorId)).data
        this.requiredDocTypes = this.driverRequirements.required_documents
        this.requiresValidation = this.driverRequirements.requires_validation
      }
    },
    checkRequiredDocuments(){
      this.missing_docs = 
        this.requiredDocTypes
        // Filter docs that have already been added
        .filter(requiredFileType => !this.files.some(driverFile => driverFile.type === requiredFileType))
        // Map to readable text
        .map(type => DropdownLists.DocumentTypesList[type])
    },
    async validateProvidedInfo() {
      this.vendorOptions = await driverFormService.getDriverFormVendorOptionsFromCapability(this.user)

      const currentVendorIds = this.user.getView().getVendor() || []
      const currentVendorId = currentVendorIds[0]

      if (currentVendorId) {
        this.$set(
          this.formValues.requiredData,
          'vendorId',
          currentVendorId
        )
      }
      this.enableVendorSelect = this.user.can('select_driver_vendor')

      if (this.driver && !driverFormService.canUpdateDriver(this.user)) {
        this.canSubmit = false
      } else if(!this.driver && !driverFormService.canSubmitNewDriver(this.user)) {
        this.canSubmit = false
      }
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_driver_form')) {
        this.$router.push({
          name: 'DriverList'
        })
        return true
      }
    },
    async setDefaultValues() {
      if (this.driverId !== undefined) {
        try {
          this.driverLoading = true
          this.driver = (await driverFormService.getDriverDetail(this.driverId)).data
          this.driverLoading = false
        }
        catch {
          this.toDriverList()
          return
        }
      }
      if (this.driver) {
        if (this.driver.user.first_name) {
          this.formValues.firstName = this.driver.user.first_name
        }
        if (this.driver.user.last_name) {
          this.formValues.lastName = this.driver.user.last_name
        }
        if (this.driver.user.email) {
          this.formValues.email = this.driver.user.email
        }
        if (this.driver.vendor.id) {
          this.$set(this.formValues.requiredData, 'vendorId', this.driver.vendor.id)
        }
        if (this.driver.date_of_birth) {
          this.formValues.dateOfBirth = this.driver.date_of_birth
        }
        if (this.driver.bio) {
          this.formValues.bio = this.driver.bio
        }
        if (this.driver.files) {
          this.files = [...this.driver.files]
        }
        this.driverFolderId = this.driver.files_folder_id
      }

      await this.getRequiredDocumentTypes()
    },
    async submitForApproval(invalid, reset) {
      this.checkRequiredDocuments()
      this.submittingForApproval = true
      await this.onSubmit(invalid, reset, true)
    }
  },
  computed:{
    editableDocuments() {
      return this.driver && this.driver.editable_documents
    },
    headerText() {
      if (this.driver && this.driver.user) {
        return `${this.driver.user.first_name} ${this.driver.user.last_name}`
      } else {
        return 'New Driver'
      }
    },
    saveText() {
      if (this.driver) {
        return 'Save'
      } else {
        return 'Next'
      }
    },
    showSubmitButton() {
      return this.requiresValidation && !this.canValidateDriver
    },
    computedFormValues() {
        return Object.assign({}, this.formValues)
    },
    // TODO: Determine proper folder name
    driverFolderName() {
      return `${this.driver.user.first_name} ${this.driver.user.last_name}`
    }
  },
  async created() {
    this.user = await user.get()
    this.canValidateDriver = this.user.can('validate_driver')

    if (this.checkUserCanViewPage()) {
      return
    }
    await this.setDefaultValues()
        
    this.validateProvidedInfo()

    detectIncognito().then((result) => {
      this.isIncognito = result.isPrivate
    });
  },
  async beforeCreate() {
    await user.ready()
    await driverFormService.getGoogleCredentials().then(response => {
      const creds = response.data.google_credentials
      this.googleCredentials.developerKey = creds.developer_key
      this.googleCredentials.appId = creds.app_id
      this.googleCredentials.clientId = creds.client_id
      this.credentialsReady = true
    }).catch(() => {})
  },
  watch: {
    googleUserEmail(val) {
      if (val) this.checkFolderPermission()
    },
    driverFolderId(val) {
      if (val && this.googleUserEmail) this.checkFolderPermission()
    },
    '$router.params.driverId': function (newValue, oldValue) {
      if (newValue !== oldValue){
        if (newValue === undefined){
          this.$router.replace({ name: 'DriverForm' })
        }
        else {
          this.$router.replace({ name: 'DriverForm', params: { driverId: newValue } })
        }
      }
    },      
  },
}
</script>